/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const UPDATE_SOURCE_REPUTATION_DISCRIMINATOR = new Uint8Array([
  161, 240, 255, 69, 130, 227, 71, 123,
]);

export function getUpdateSourceReputationDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_SOURCE_REPUTATION_DISCRIMINATOR,
  );
}

export type UpdateSourceReputationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountReputationMetrics extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountReputationMetrics extends string
        ? WritableAccount<TAccountReputationMetrics>
        : TAccountReputationMetrics,
      TAccountAgent extends string
        ? WritableAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateSourceReputationInstructionData = {
  discriminator: ReadonlyUint8Array;
  sourceName: string;
  score: number;
  weight: number;
  dataPoints: number;
  reliability: number;
};

export type UpdateSourceReputationInstructionDataArgs = {
  sourceName: string;
  score: number;
  weight: number;
  dataPoints: number;
  reliability: number;
};

export function getUpdateSourceReputationInstructionDataEncoder(): Encoder<UpdateSourceReputationInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["sourceName", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["score", getU16Encoder()],
      ["weight", getU16Encoder()],
      ["dataPoints", getU32Encoder()],
      ["reliability", getU16Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: UPDATE_SOURCE_REPUTATION_DISCRIMINATOR,
    }),
  );
}

export function getUpdateSourceReputationInstructionDataDecoder(): Decoder<UpdateSourceReputationInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["sourceName", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["score", getU16Decoder()],
    ["weight", getU16Decoder()],
    ["dataPoints", getU32Decoder()],
    ["reliability", getU16Decoder()],
  ]);
}

export function getUpdateSourceReputationInstructionDataCodec(): Codec<
  UpdateSourceReputationInstructionDataArgs,
  UpdateSourceReputationInstructionData
> {
  return combineCodec(
    getUpdateSourceReputationInstructionDataEncoder(),
    getUpdateSourceReputationInstructionDataDecoder(),
  );
}

export type UpdateSourceReputationAsyncInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics?: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (can be oracle, agent owner, or authorized updater) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  sourceName: UpdateSourceReputationInstructionDataArgs["sourceName"];
  score: UpdateSourceReputationInstructionDataArgs["score"];
  weight: UpdateSourceReputationInstructionDataArgs["weight"];
  dataPoints: UpdateSourceReputationInstructionDataArgs["dataPoints"];
  reliability: UpdateSourceReputationInstructionDataArgs["reliability"];
};

export async function getUpdateSourceReputationInstructionAsync<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateSourceReputationAsyncInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UpdateSourceReputationInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.reputationMetrics.value) {
    accounts.reputationMetrics.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 112, 117, 116, 97, 116, 105, 111, 110, 95, 109, 101, 116,
            114, 105, 99, 115,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateSourceReputationInstructionDataEncoder().encode(
      args as UpdateSourceReputationInstructionDataArgs,
    ),
    programAddress,
  } as UpdateSourceReputationInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >);
}

export type UpdateSourceReputationInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (can be oracle, agent owner, or authorized updater) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  sourceName: UpdateSourceReputationInstructionDataArgs["sourceName"];
  score: UpdateSourceReputationInstructionDataArgs["score"];
  weight: UpdateSourceReputationInstructionDataArgs["weight"];
  dataPoints: UpdateSourceReputationInstructionDataArgs["dataPoints"];
  reliability: UpdateSourceReputationInstructionDataArgs["reliability"];
};

export function getUpdateSourceReputationInstruction<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateSourceReputationInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): UpdateSourceReputationInstruction<
  TProgramAddress,
  TAccountReputationMetrics,
  TAccountAgent,
  TAccountAuthority,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateSourceReputationInstructionDataEncoder().encode(
      args as UpdateSourceReputationInstructionDataArgs,
    ),
    programAddress,
  } as UpdateSourceReputationInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >);
}

export type ParsedUpdateSourceReputationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Reputation metrics account */
    reputationMetrics: TAccountMetas[0];
    /** Agent account */
    agent: TAccountMetas[1];
    /** Authority (can be oracle, agent owner, or authorized updater) */
    authority: TAccountMetas[2];
    /** Clock for timestamps */
    clock: TAccountMetas[3];
  };
  data: UpdateSourceReputationInstructionData;
};

export function parseUpdateSourceReputationInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateSourceReputationInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      reputationMetrics: getNextAccount(),
      agent: getNextAccount(),
      authority: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getUpdateSourceReputationInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
